import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faTimes, faCheck, faCircleNotch, faAtom, faCode, faFlask, faDna, faCog, faStream, faVial, faDatabase, faDesktop, faFileAlt, faChevronRight, faAngleDoubleRight, faSearch, faFilter, faChartBar, faExternalLinkAlt, faExclamationTriangle, faCodeCompare, faUsers, faCheckCircle, faSearchPlus, faTimes as faTimesX, faMapLocation, faImagePortrait, faImage} from '@fortawesome/free-solid-svg-icons';
import './OmicsFrame.css';
import ImageVisualizer from './ImageVisualizer';
import ReactMarkdown from 'react-markdown';

// API base URL should match your backend
const API_BASE_URL = 'https://service4.szapfs.org';

// Create a context for sharing the updateSegmentHeights function
const SegmentHeightContext = createContext();



const OmicsFrame = ({ 
    omicsFrameData, 
    onClose, 
    conversationId 
}) => {
    const [steps, setSteps] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [frameDetails, setFrameDetails] = useState({
        id: omicsFrameData.omics_frame_id,
        name: "Multi-Omics Analysis",
        summary: omicsFrameData.summary,
    });
    const pollIntervalRef = useRef(null);
    const summaryPollIntervalRef = useRef(null);
    const outputBoxRefs = useRef({});
    const [segmentHeights, setSegmentHeights] = useState({});
    const [modalState, setModalState] = useState({ isOpen: false, content: '', title: '' });
    const [summaryVisible, setSummaryVisible] = useState(false);
    const [summaryFadedIn, setSummaryFadedIn] = useState(false);
    
    console.log(`[OmicsFrame] Component mounted for frame: ${omicsFrameData.omics_frame_id}`);

    // Load OmicsFrame data on component mount
    useEffect(() => {
        const frameId = omicsFrameData.omics_frame_id;
        
        if (frameId) {
            console.log(`[OmicsFrame] Loading frame ID: ${frameId}`);
            fetchOmicsFrameDetails(frameId);
            startPolling(frameId);
            startSummaryPolling(frameId);
        }

        // Clean up polling on unmount
        return () => {
            stopPolling();
            stopSummaryPolling();
            console.log(`[OmicsFrame] Component unmounted, all polling stopped for frame ID: ${frameId}`);
        };
    }, [omicsFrameData.omics_frame_id]);

    // Separate function to stop polling to ensure it's done consistently
    const stopPolling = () => {
        if (pollIntervalRef.current) {
            console.log(`[OmicsFrame] Stopping poll interval: ${pollIntervalRef.current}`);
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    // Stop summary polling
    const stopSummaryPolling = () => {
        if (summaryPollIntervalRef.current) {
            console.log(`[OmicsFrame] Stopping summary poll interval: ${summaryPollIntervalRef.current}`);
            clearInterval(summaryPollIntervalRef.current);
            summaryPollIntervalRef.current = null;
        }
    };

    const fetchOmicsFrameDetails = async (frameId) => {
        try {
            console.log(`[OmicsFrame] Fetching details for frame ID: ${frameId}`);
            setIsLoading(true);
            const response = await fetch(`${API_BASE_URL}/omicsframe/${frameId}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log(`[OmicsFrame] Received details for frame ID: ${frameId}`, data);
            
            if (data.omicsframe) {
                // Check if we have a meaningful summary
                const hasSummary = data.omicsframe.summary && data.omicsframe.summary.trim().length > 0;
                
                // Update frame details including name
                setFrameDetails({
                    id: data.omicsframe.id,
                    name: data.omicsframe.name || "Multi-Omics Analysis",
                    summary: data.omicsframe.summary || ""
                });
                
                // If summary is available, show it with a fade effect
                if (hasSummary) {
                    setSummaryVisible(true);
                    // Small delay to trigger the fade-in effect after visibility is set
                    setTimeout(() => {
                        setSummaryFadedIn(true);
                    }, 100);
                }
                
                if (data.omicsframe.steps) {
                    setSteps(data.omicsframe.steps);
                }
            }
        } catch (error) {
            console.error(`[OmicsFrame] Error fetching OmicsFrame details for frame ID: ${frameId}:`, error);
        } finally {
            setIsLoading(false);
        }
    };

    const startPolling = (frameId) => {
        // First, ensure any existing polling is stopped
        stopPolling();
        
        console.log(`[OmicsFrame] Starting polling for frame ID: ${frameId}`);
        
        // Poll every 1 second for step updates
        const interval = setInterval(() => {
            // Check if the component is still mounted by verifying the interval is still the same
            if (pollIntervalRef.current === interval) {
                console.log(`[OmicsFrame] Polling steps for frame ID: ${frameId}`);
                pollOmicsFrameSteps(frameId);
            } else {
                // If interval doesn't match, it means a new poll has been started or we need to stop
                console.log(`[OmicsFrame] Stale polling detected, cleaning up`);
                clearInterval(interval);
            }
        }, 1000);
        
        pollIntervalRef.current = interval;
    };

    const startSummaryPolling = (frameId) => {
        // First, ensure any existing polling is stopped
        stopSummaryPolling();
        
        console.log(`[OmicsFrame] Starting summary polling for frame ID: ${frameId}`);
        
        // Poll every 2 seconds for summary updates
        const interval = setInterval(() => {
            // Check if the component is still mounted by verifying the interval is still the same
            if (summaryPollIntervalRef.current === interval) {
                console.log(`[OmicsFrame] Polling summary for frame ID: ${frameId}`);
                pollOmicsFrameSummary(frameId);
            } else {
                // If interval doesn't match, it means a new poll has been started or we need to stop
                console.log(`[OmicsFrame] Stale summary polling detected, cleaning up`);
                clearInterval(interval);
            }
        }, 2000);
        
        summaryPollIntervalRef.current = interval;
    };

    const pollOmicsFrameSummary = async (frameId) => {
        // Extra safety check - don't poll if no interval is set
        if (!summaryPollIntervalRef.current) {
            console.log(`[OmicsFrame] Summary polling attempted but no poll interval is active`);
            return;
        }
        
        try {
            const response = await fetch(`${API_BASE_URL}/omicsframe/${frameId}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.omicsframe) {
                // Check if we have a meaningful summary
                const newSummary = data.omicsframe.summary || "";
                const hasSummary = newSummary && newSummary.trim().length > 0;
                
                // Only update if the summary is meaningful and different from current
                if (hasSummary && newSummary !== frameDetails.summary) {
                    console.log(`[OmicsFrame] Received new summary for frame ID: ${frameId}`);
                    
                    // Update the frame details with new summary
                    setFrameDetails(prev => ({
                        ...prev,
                        summary: newSummary
                    }));
                    
                    // Make it visible and trigger fade-in effect
                    setSummaryVisible(true);
                    setTimeout(() => {
                        setSummaryFadedIn(true);
                    }, 100);
                    
                    // Stop polling for summary as we've found it
                    if (hasSummary) {
                        console.log(`[OmicsFrame] Summary found, stopping polling`);
                        stopSummaryPolling();
                    }
                }
            }
        } catch (error) {
            console.error(`[OmicsFrame] Error polling OmicsFrame summary for frame ID: ${frameId}:`, error);
            // Don't stop polling on errors - it might be temporary
        }
    };

    const pollOmicsFrameSteps = async (frameId) => {
        // Extra safety check - don't poll if no interval is set
        if (!pollIntervalRef.current) {
            console.log(`[OmicsFrame] Polling attempted but no poll interval is active`);
            return;
        }
        
        try {
            const response = await fetch(`${API_BASE_URL}/omicsframe/${frameId}/steps`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.steps) {
                console.log(`[OmicsFrame] Received ${data.steps.length} steps for frame ID: ${frameId}`);
                setSteps(data.steps);
                
                // Check if all steps are completed or failed
                const allDone = data.steps.every(step => 
                    step.status === 'completed' || step.status === 'failed');
                
                if (allDone) {
                    // When all steps are done, continue polling for summary a bit longer
                    console.log(`[OmicsFrame] All steps completed for frame ID: ${frameId}, stopping steps polling`);
                    stopPolling();
                    
                    // Make sure summary polling is still active
                    if (!summaryPollIntervalRef.current) {
                        startSummaryPolling(frameId);
                    }
                }
            }
        } catch (error) {
            console.error(`[OmicsFrame] Error polling OmicsFrame steps for frame ID: ${frameId}:`, error);
            // Don't stop polling on errors - it might be temporary
        }
    };

    // Update segment line heights based on output parameter box height
    const updateSegmentHeights = () => {
        const newHeights = {};
        Object.keys(outputBoxRefs.current).forEach(stepId => {
            const boxEl = outputBoxRefs.current[stepId];
            if (boxEl) {
                const boxHeight = boxEl.offsetHeight;
                // Add 3vh to the output parameter box height
                const vh = window.innerHeight / 100;
                newHeights[stepId] = boxHeight + (3.5 * vh);
            }
        });
        setSegmentHeights(newHeights);
    };

    // Effect to update segment heights when steps change or window resizes
    useEffect(() => {
        // Run once after render
        updateSegmentHeights();
        
        // Also run on window resize
        window.addEventListener('resize', updateSegmentHeights);
        return () => window.removeEventListener('resize', updateSegmentHeights);
    }, [steps]);

    // Get appropriate icon for step category
    const getStepIcon = (toolName, category) => {
        const toolNameLower = toolName?.toLowerCase() || '';
        const categoryLower = category?.toLowerCase() || '';
        
        if (categoryLower.includes('cohort')) {
            return faUsers;
        } else if (categoryLower.includes('visualize')) {
            return faImage;
        } else if (categoryLower.includes('proteomics')) {
            return faVial;
        } else if (categoryLower.includes('metabolomics')) {
            return faFlask;
        } else if (categoryLower.includes('cohort')) {
            return faDatabase;
        } else {
            return faAtom;
        }
    };

    // Get status icon
    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <FontAwesomeIcon icon={faCheckCircle} className="status-icon completed" />;
            case 'running':
                return <FontAwesomeIcon icon={faCircleNotch} className="status-icon running spin-icon" />;
            case 'failed':
                return <FontAwesomeIcon icon={faExclamationTriangle} className="status-icon failed" />;
            case 'pending':
                return <FontAwesomeIcon icon={faChevronRight} className="status-icon pending" />;
            default:
                return null;
        }
    };

    return (
        <SegmentHeightContext.Provider value={{ updateSegmentHeights }}>
            <div className="omics-frame-container">
                <div className="omics-frame-header">
                    <button className="omics-frame-close" onClick={() => {
                        console.log(`[OmicsFrame] Close button clicked for frame: ${frameDetails.id}`);
                        stopPolling();
                        onClose();
                    }}>
                        <FontAwesomeIcon icon={faAngleDoubleRight} />
                    </button>
                    {frameDetails.name}
                </div>
                
                <div className="omics-frame-content">
                    {isLoading ? (
                        <div className="loading-indicator">
                            <FontAwesomeIcon icon={faCircleNotch} className="spin-icon" />
                            <p>Loading analysis steps...</p>
                        </div>
                    ) : steps.length === 0 ? (
                        <p>No analysis steps found.</p>
                    ) : (
                        <div className="timeline-container">
                            <div className="timeline-steps">
                                {steps.map((step, index) => (
                                    <React.Fragment key={step.id}>
                                        <div 
                                            className={`timeline-step ${step.status} ${step.grayedOut ? 'grayed-out' : ''}`}
                                            style={{ 
                                                animationDelay: `${0.1 * (index + 1)}s`
                                            }}
                                        >
                                            <div className="step-content">
                                                <div className="step-node">
                                                    <FontAwesomeIcon icon={getStepIcon(step.tool_name, step.category)} />
                                                </div>
                                
                                                <div className="step-category">
                                                    {step.category}
                                                    {step.subcategory && (
                                                        <span className="step-subcategory"> {step.subcategory}</span>
                                                    )}
                                                </div>
                                                <div className="step-title">
                                                    <div className="step-status-icon">
                                                    {getStatusIcon(step.status)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {index < steps.length && (
                                            <div className="segment-line-container">
                                                <div 
                                                    className={`segment-line ${step.status}`}
                                                    style={{
                                                        height: segmentHeights[step.id] ? `${segmentHeights[step.id]}px` : '3vh',
                                                        animationDelay: `${0.1 * (index + 1) + 0.2}s`
                                                    }}
                                                ></div>
                                                {step.status !== 'pending' && (
                                                    <div className="output-parameter-box"
                                                        ref={el => {
                                                            if (el) outputBoxRefs.current[step.id] = el;
                                                        }}
                                                        style={{
                                                            animationDelay: `${0.1 * (index + 1) + 0.3}s`
                                                        }}
                                                    >
                                                        {step.status === 'running' ? (
                                                            <div className="output-loading">
                                                                <FontAwesomeIcon icon={faCircleNotch} className="spin-icon" />
                                                                <span>Processing...</span>
                                                            </div>
                                                        ) : step.status === 'completed' && step.output ? (
                                                            <div className="parameter-content">
                                                                {(() => {
                                                                    try {
                                                                        // Try to parse the output as JSON
                                                                        const outputData = typeof step.output === 'string' ? 
                                                                            JSON.parse(step.output) : step.output;
                                                                        
                                                                        // Check for the special case: {"result": "image"}
                                                                        if (outputData && 
                                                                            Object.keys(outputData).length === 1 && 
                                                                            outputData.result === "image") {
                                                                            
                                                                            // This is a visualization that needs to be fetched
                                                                            return <StepVisualization 
                                                                                     frameId={frameDetails.id} 
                                                                                     stepNumber={index + 1} 
                                                                                   />;
                                                                        }
                                                                        
                                                                        return Object.entries(outputData).map(([key, value], idx) => {
                                                                            // Convert value to string representation for display
                                                                            let displayValue = typeof value === 'object' && value !== null 
                                                                                ? JSON.stringify(value) 
                                                                                : String(value);
                                                                            
                                                                            // Check if value is longer than 30 characters
                                                                            const isLongValue = displayValue.length > 30;
                                                                            const truncatedValue = isLongValue ? displayValue.substring(0, 30) + '...' : displayValue;
                                                                            
                                                                            // Format key for display
                                                                            const displayKey = key.charAt(0).toUpperCase() + key.slice(1);
                                                                            
                                                                            return (
                                                                                <div key={idx} className="parameter-item">
                                                                                    <div className="param-label">{displayKey}:</div>
                                                                                    <div className="param-value">
                                                                                        {isLongValue ? (
                                                                                            <>
                                                                                                <span className="truncated-value">{truncatedValue}</span>
                                                                                                <button 
                                                                                                    className="view-more-btn"
                                                                                                    onClick={() => setModalState({ 
                                                                                                        isOpen: true, 
                                                                                                        content: displayValue,
                                                                                                        title: displayKey
                                                                                                    })}
                                                                                                >
                                                                                                    <FontAwesomeIcon icon={faSearchPlus} />
                                                                                                </button>
                                                                                            </>
                                                                                        ) : (
                                                                                            displayValue
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        });
                                                                    } catch (e) {
                                                                        // If parsing fails, just display the raw output
                                                                        return (
                                                                            <div className="parameter-item">
                                                                                <div className="param-value">{step.output}</div>
                                                                            </div>
                                                                        );
                                                                    }
                                                                })()}
                                                            </div>
                                                        ) : (
                                                            <div className="no-parameters">
                                                                {step.status === 'failed' ? 'Step failed' : 'No output available'}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {step.status !== 'pending' && (
                                                    <div className="input-parameter-box"
                                                    
                                                        style={{
                                                            animationDelay: `${0.1 * (index + 1) + 0.3}s`
                                                        }}
                                                    >
                                                        {step.status === 'completed' && step.output ? (
                                                            <div className="parameter-content">
                                                                {(() => {
                                                                    try {
                                                                        // Try to parse the output as JSON
                                                                        const parametersData = typeof step.parameters === 'string' ? 
                                                                            JSON.parse(step.parameters) : step.parameters;
                                                                        
                                                                        return Object.entries(parametersData).map(([key, value], idx) => {
                                                                            // Convert value to string representation for display
                                                                            let displayValue = typeof value === 'object' && value !== null 
                                                                                ? JSON.stringify(value) 
                                                                                : String(value);
                                                                            
                                                                            // Check if value is longer than 30 characters
                                                                            const isLongValue = displayValue.length > 30;
                                                                            const truncatedValue = isLongValue ? displayValue.substring(0, 30) + '...' : displayValue;
                                                                            
                                                                            // Format key for display
                                                                            const displayKey = key.charAt(0).toUpperCase() + key.slice(1);
                                                                            
                                                                            return (
                                                                                <div key={idx} className="parameter-item">
                                                                                    <div className="param-label">{displayKey}:</div>
                                                                                    <div className="param-value">
                                                                                        {isLongValue ? (
                                                                                            <>
                                                                                                <span className="truncated-value">{truncatedValue}</span>
                                                                                                <button 
                                                                                                    className="view-more-btn"
                                                                                                    onClick={() => setModalState({ 
                                                                                                        isOpen: true, 
                                                                                                        content: displayValue,
                                                                                                        title: displayKey
                                                                                                    })}
                                                                                                >
                                                                                                    <FontAwesomeIcon icon={faSearchPlus} />
                                                                                                </button>
                                                                                            </>
                                                                                        ) : (
                                                                                            displayValue
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        });
                                                                    } catch (e) {
                                                                        // If parsing fails, just display the raw output
                                                                        return (
                                                                            <div className="parameter-item">
                                                                                <div className="param-value">{step.parameters}</div>
                                                                            </div>
                                                                        );
                                                                    }
                                                                })()}
                                                            </div>
                                                        ) : (
                                                            <div className="no-parameters">
                                                                {step.status === 'failed' ? 'Step failed' : 'No output available'}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                
                                            </div>

                                            
                                        )}
                                        {index === steps.length - 1 && (
                                            <div 
                                                className={`conclusiondot ${step.status}`}
                                                style={{
                                                    animationDelay: `${0.1 * (index + 1) + 0.2}s`
                                                }}
                                            ></div>
                                        )}
                                        {index === steps.length - 1 && (
  <div
    className="summary-parameters"
    style={{ animationDelay: `${0.1 * steps.length + 0.4}s` }}
  >
    {frameDetails.summary ? (
      <ReactMarkdown>{frameDetails.summary}</ReactMarkdown>
    ) : (
      <div className="summary-loading">
        <FontAwesomeIcon icon={faCircleNotch} className="spin-icon" />
      </div>
    )}
  </div>
)}

                                    </React.Fragment>
                                ))}
                            
                            </div>
                 

                        </div>
                        
                    )}
              
                </div>
                
                {/* Add the parameter modal */}
                <ParameterModal 
                    isOpen={modalState.isOpen}
                    onClose={() => setModalState({ ...modalState, isOpen: false })}
                    content={modalState.content}
                    title={modalState.title}
                />
            </div>
        </SegmentHeightContext.Provider>
    );
};

// Simple Modal component for displaying long parameter values
const ParameterModal = ({ isOpen, onClose, content, title }) => {
    if (!isOpen) return null;
    
    return (
        <div className="parameter-modal-overlay" onClick={onClose}>
            <div className="parameter-modal-content" onClick={e => e.stopPropagation()}>
                <div className="parameter-modal-header">
                    <h3>{title}</h3>
                    <button className="parameter-modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimesX} />
                    </button>
                </div>
                <div className="parameter-modal-body">
                    {content}
                </div>
            </div>
        </div>
    );
};

// StepVisualization component to handle fetching and displaying the image
const StepVisualization = ({ frameId, stepNumber }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isVisualizerOpen, setIsVisualizerOpen] = useState(false);
    const imageRef = useRef(null);
    
    // Get reference to the parent component's updateSegmentHeights function
    const { updateSegmentHeights } = useContext(SegmentHeightContext) || {};
    
    useEffect(() => {
        const fetchVisualization = async () => {
            try {
                setIsLoading(true);
                setError(null);
                
                // Fetch the visualization parameters
                const response = await fetch(
                    `${API_BASE_URL}/omicsframe/${frameId}/step/${stepNumber}/visualization`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    }
                );
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch visualization: ${response.status}`);
                }
                
                // Set the image URL directly from the response
                // For security, we're constructing the URL using the known endpoint
                setImageUrl(`${API_BASE_URL}/omicsframe/${frameId}/step/${stepNumber}/visualization`);
                
            } catch (err) {
                console.error('Error fetching visualization:', err);
                setError('Failed to load visualization');
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchVisualization();
    }, [frameId, stepNumber]);
    
    // Handle image load event to trigger segment line update
    const handleImageLoad = () => {
        // Call updateSegmentHeights after image loads
        if (updateSegmentHeights) {
            // Give a small delay to ensure image is rendered properly
            setTimeout(updateSegmentHeights, 100);
        }
    };
    
    // Handle image click to open the visualizer
    const handleImageClick = () => {
        setIsVisualizerOpen(true);
    };
    
    // Handle closing the visualizer
    const handleCloseVisualizer = () => {
        setIsVisualizerOpen(false);
    };
    
    if (isLoading) {
        return (
            <div className="visualization-loading">
                <FontAwesomeIcon icon={faCircleNotch} className="spin-icon" />
                <span>Loading visualization...</span>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="visualization-error">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{error}</span>
            </div>
        );
    }
    
    return (
        <>
            <div className="visualization-container">
                <img 
                    ref={imageRef}
                    src={imageUrl} 
                    alt={`Step ${stepNumber} Visualization`} 
                    className="visualization-image"
                    onLoad={handleImageLoad}
                    onClick={handleImageClick}
                    onError={() => setError('Failed to load visualization image')}
                />
            </div>
            
            {isVisualizerOpen && (
                <ImageVisualizer 
                    imageUrl={imageUrl} 
                    onClose={handleCloseVisualizer} 
                />
            )}
        </>
    );
};

export default OmicsFrame;
